import { day } from '@vrplatform/utils';
import { Text } from '@mantine/core';
import { type ReactNode, useMemo } from 'react';
import type { BalanceSheetRow } from './useBalanceSheetTableQuery';
import { useBalanceSheetTableFilter } from './useBalanceSheetTableFilter';
import type { EmotionSx } from '@mantine/emotion';
import type { api } from '@vrplatform/data';

export type BalanceSheetJournalEntriesArgs = {
  accountId?: string;
  categoryId?: string;
  filter?: api['/reports/journal-entries']['get']['parameters']['query'];
};

const Base = ({
  type,
  children,
  align,
  size = 'sm',
  breakWord = false,
  onClick,
}: {
  depth: number;
  children: ReactNode;
  align: 'left' | 'right';
  size?: 'md' | 'sm';
  breakWord?: boolean;
  type: BalanceSheetRow['type'];
  onClick?: () => void;
}) => {
  const sx = useMemo<EmotionSx>(() => {
    const breakWordStyle: EmotionSx | undefined = breakWord
      ? {
          display: 'block',
          wordBreak: 'break-all',
          whiteSpace: 'normal',
          lineBreak: 'anywhere',
        }
      : undefined;

    const onClickStyle: EmotionSx | undefined = onClick
      ? {
          cursor: 'pointer',
          transition: 'text-decoration 0.2s ease-in-out',
          ':hover': {
            textDecoration: 'underline',
          },
        }
      : undefined;
    return {
      ...breakWordStyle,
      ...onClickStyle,
    };
  }, [breakWord, onClick]);

  return (
    <Text
      fw={type === 'group' ? 'bold' : 'normal'}
      size={size}
      ta={align}
      sx={sx}
      onClick={onClick}
    >
      {children}
    </Text>
  );
};

const Number = ({
  children,
  columndId,
  depth,
  onOpenJournalEntries,
  row,
}: {
  row: BalanceSheetRow;
  columndId: string;
  children: ReactNode;
  depth: number;
  onOpenJournalEntries?: (args: BalanceSheetJournalEntriesArgs) => void;
}) => {
  const {
    filter: { type: view, startDate: globalStartDate, endDate: globalEndDate },
  } = useBalanceSheetTableFilter();

  const onClick = useMemo<(() => void) | undefined>(() => {
    if (!onOpenJournalEntries || (!row.accountId && !row.categoryId)) {
      return undefined;
    }

    const base: api['/reports/journal-entries']['get']['parameters']['query'] =
      {
        accountIds: row.accountId,
        categoryIds: row.categoryId,
      };

    if (view === 'month') {
      // columnId format: YYYY-MM
      const startDate = `${columndId}-01`;
      const endDate = day(startDate).endOf('month').yyyymmdd();
      base.startAt = startDate;
      base.endAt = endDate;
    }

    if (view === 'quarter') {
      // columnId format: YYYY-Q1 or YYYY-Q2 or YYYY-Q3 or YYYY-Q4
      const split = columndId.split('-');
      const year = split[0];
      const quarter = split[1];
      const parsed = parseInt(quarter.at(1) || '1', 10);
      const month = parsed === 1 ? 1 : parsed === 2 ? 4 : parsed === 3 ? 7 : 10;
      let startDate = `${year}-${month < 10 ? `0${month}` : month}-01`;
      let endDate = day(startDate).add(2, 'month').endOf('month').yyyymmdd();

      if (day(startDate).isBefore(globalStartDate, 'day')) {
        startDate = globalStartDate;
      }
      if (day(endDate).isAfter(globalEndDate, 'day')) {
        endDate = globalEndDate;
      }

      base.startAt = startDate;
      base.endAt = endDate;
    }

    if (view === 'year') {
      // columnId format: YYYY
      const startDate = `${columndId}-01-01`;
      const endDate = day(startDate).endOf('year').yyyymmdd();
      base.startAt = startDate;
      base.endAt = endDate;
    }

    return () =>
      onOpenJournalEntries({
        accountId: row.accountId,
        categoryId: row.categoryId,
        filter: base,
      });
  }, [
    onOpenJournalEntries,
    row.categoryId,
    row.accountId,
    columndId,
    view,
    globalStartDate,
    globalEndDate,
  ]);

  return (
    <Base type={row.type} depth={depth} align="right" onClick={onClick}>
      {children}
    </Base>
  );
};

export const BalanceSheetTableCell = {
  Base,
  Number,
};
